import React from "react";
import { Control, Controller, FieldError, FieldValues, Path, useController } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { TextField, FormHelperText } from "@mui/material";

type AllowedPrefix = "$" | "%" | "UF" | "+56" | "+569" | "CLP" | "días" | "años" | "meses" | "mes" | "m2" | "UF SAC";

interface InputNumberProps<K extends FieldValues> {
    name: Path<K>;
    control: Control<K>;
    error?: FieldError;
    prefix?: AllowedPrefix;
    disabled?: boolean;
    readonly?: boolean;
    decimales?: number;
    fixedDecimalScale?: boolean;
    suffix?: AllowedPrefix;
    minValue?: number;
    maxValue?: number;
    presicion?: number;
    esMoneda?: boolean;
    label?: string | React.ReactNode;
    onChange?: (value: number | null) => void;
    allowNegative?: boolean;
}

export default function InputNumber<K extends FieldValues>({
    name,
    control,
    error,
    prefix,
    disabled,
    readonly,
    decimales = 0,
    fixedDecimalScale,
    suffix,
    minValue,
    maxValue = 999999999999999999,
    presicion = 18,
    esMoneda,
    label,
    onChange,
    allowNegative = false
}: InputNumberProps<K>) {
    const { field } = useController({ name, control });

    function validarPrecision(number: string): boolean {
        const digits = number.replace(".", "");
        return digits.length <= presicion;
    }

    let separador: string = prefix === "+56" || prefix === "+569" || prefix === "%" || !prefix ? "" : ".";
    if (esMoneda) separador = ".";
    const effectiveMinValue = minValue ?? (allowNegative ? -maxValue : 0);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange: rhfOnChange, onBlur, value: fieldValue } }) => (
                <>
                    <NumericFormat
                        value={fieldValue ?? ""}
                        onValueChange={(v) => {
                            const valorFinal = v.floatValue === undefined ? null : v.floatValue;
                            rhfOnChange(valorFinal);
                            onChange?.(valorFinal);
                        }}
                        customInput={TextField}
                        label={label}
                        fullWidth
                        variant="standard"
                        disabled={disabled}
                        prefix={prefix && `${prefix} `}
                        decimalScale={decimales}
                        fixedDecimalScale={fixedDecimalScale}
                        allowNegative={allowNegative}
                        thousandSeparator={separador}
                        decimalSeparator=","
                        suffix={suffix && ` ${suffix}`}
                        autoComplete="off"
                        isAllowed={(values) => {
                            const { floatValue, value } = values;
                            return floatValue === undefined || (validarPrecision(value) && floatValue >= effectiveMinValue && floatValue <= maxValue);
                        }}
                        onBlur={onBlur}
                        error={!!error}
                        sx={{
                            "& .MuiInputBase-root": { height: "40px" }
                        }}
                        slotProps={{ input: { readOnly: readonly } }}
                    />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                </>
            )}
        />

    );
}
